import { useCallback } from 'react';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import { useTableStoreContext } from '@/components/table/contexts/TableContext.tsx';
import { exportRowsToExcel } from '@/components/table/_export/TableExport.ts';
import {
  getColumnId,
  isVisibleColumn,
  OPTIONS_COLUMN_ID,
  SELECT_COLUMN_ID,
} from '@/components/table/preferences/columnPreferences.ts';

/**
 * Exports the selected rows with the columns the user currently sees, in their
 * order, without the system (select / options) columns.
 */
export const useSelectionExport = <T extends RowWithId>(rows: T[]) => {
  const tableDefinition = useTableStoreContext(
    (state) => state.tableDefinition,
  );
  const columnOrder = useTableStoreContext((state) => state.columnOrder);
  const columnVisibility = useTableStoreContext(
    (state) => state.columnVisibility,
  );

  return useCallback(() => {
    const dataColumnIds = new Set(
      (tableDefinition.fields ?? []).filter(isVisibleColumn).map(getColumnId),
    );
    const orderedFieldIds = (
      columnOrder.length > 0 ? columnOrder : [...dataColumnIds]
    ).filter(
      (id) =>
        id !== SELECT_COLUMN_ID &&
        id !== OPTIONS_COLUMN_ID &&
        dataColumnIds.has(id) &&
        columnVisibility[id] !== false,
    );

    exportRowsToExcel({ rows, tableDefinition, orderedFieldIds });
  }, [rows, tableDefinition, columnOrder, columnVisibility]);
};
